import React, { useEffect, useState } from 'react';
import { ArrowLeft, CalendarDays, Loader2, Tag } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { getBlogs } from '../../services/blogs';

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const listResponse = await getBlogs({ limit: 1000 });
        const matchedBlog = (listResponse.blogs || []).find((item) => item.slug === slug);

        if (!matchedBlog) {
          setError('Blog not found.');
          setBlog(null);
          setRelatedBlogs([]);
          return;
        }

        const currentBlog = matchedBlog;
        setBlog(currentBlog);

        setRelatedBlogs(
          (listResponse.blogs || [])
            .filter((item) => item._id !== currentBlog._id && item.category === currentBlog.category)
            .slice(0, 3)
        );
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load blog.');
      } finally {
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    fetchBlog();
  }, [slug]);

  return (
    <section className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pt-32 pb-20">
      <div className="container mx-auto px-4">
        <Link to="/blogs" className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200">
          <ArrowLeft className="w-4 h-4" />
          Back to blogs
        </Link>

        {loading && (
          <div className="mt-10 flex items-center gap-3 text-slate-200">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading article...</span>
          </div>
        )}

        {!loading && error && (
          <div className="mt-10 rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-5 text-red-100">
            {error}
          </div>
        )}

        {!loading && !error && blog && (
          <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
            <article className="rounded-[2rem] border border-white/10 bg-white/5 p-8 md:p-12 backdrop-blur-sm">
              <span className="inline-flex rounded-full bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
                {blog.category}
              </span>

              <h1 className="mt-6 text-4xl md:text-5xl font-bold text-white leading-tight">
                {blog.title}
              </h1>

              <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-400">
                {blog.publishedAt && (
                  <span className="inline-flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />
                    {formatDate(blog.publishedAt)}
                  </span>
                )}
                {blog.tags?.length > 0 && (
                  <span className="inline-flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    {blog.tags.join(', ')}
                  </span>
                )}
              </div>

              {blog.excerpt && (
                <p className="mt-8 text-lg leading-8 text-slate-300 border-l-2 border-cyan-400/60 pl-5">
                  {blog.excerpt}
                </p>
              )}

              <div
                className="prose prose-invert text-white prose-headings:text-white prose-p:text-white prose-li:text-white prose-strong:text-white max-w-none mt-10"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
            </article>

            <aside className="h-fit rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
              <h2 className="text-xl font-semibold text-white">More from this category</h2>

              <div className="mt-6 space-y-5">
                {relatedBlogs.length === 0 && (
                  <p className="text-slate-400">No related published blogs yet.</p>
                )}

                {relatedBlogs.map((item) => (
                  <Link
                    key={item._id}
                    to={`/blogs/${item.slug}`}
                    className="block rounded-2xl border border-white/10 bg-slate-900/50 p-5 transition-colors hover:border-cyan-400/40 hover:bg-slate-900"
                  >
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-300">{item.category}</p>
                    <h3 className="mt-2 text-lg font-semibold text-white">{item.title}</h3>
                    <p className="mt-2 text-sm text-slate-400">
                      {item.excerpt || 'Read the full article.'}
                    </p>
                  </Link>
                ))}
              </div>
            </aside>
          </div>
        )}
      </div>
    </section>
  );
}
